import React, { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { useOverlayA11y } from '../hooks/useOverlayA11y'
import { MfaRecoveryCodesModal } from './MfaRecoveryCodesModal'

/** Parses the shared-secret out of the otpauth:// provisioning URI for the
 * "can't scan? enter this manually" fallback — the backend deliberately
 * never returns the raw secret on its own, only embedded in this URI and
 * the QR code rendered from it. */
function parseSecret(provisioningUri: string): string {
  try {
    const query = provisioningUri.split('?')[1] || ''
    return new URLSearchParams(query).get('secret') || ''
  } catch {
    return ''
  }
}

export function MfaSetupModal({ onClose, onEnabled }: { onClose: () => void; onEnabled: () => void }) {
  const [stage, setStage] = useState<'loading' | 'scan' | 'codes' | 'error'>('loading')
  const [qrDataUri, setQrDataUri] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const modalRef = useOverlayA11y(onClose)

  useEffect(() => {
    // If this app is ever run under React.StrictMode (dev double-invokes
    // effects), this flag makes the loser of the resulting race a no-op
    // instead of letting whichever settles last clobber the other's state.
    let cancelled = false
    api('/mfa/enroll', { method: 'POST' })
      .then(({ provisioning_uri, qr_code_data_uri }) => {
        if (cancelled) return
        setQrDataUri(qr_code_data_uri)
        setSecret(parseSecret(provisioning_uri))
        setStage('scan')
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Could not start enrollment.')
        setStage('error')
      })
    return () => { cancelled = true }
  }, [])

  async function submitCode(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { recovery_codes } = await api('/mfa/verify-setup', { method: 'POST', body: JSON.stringify({ code }) })
      setRecoveryCodes(recovery_codes)
      setStage('codes')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Incorrect code.')
    } finally {
      setBusy(false)
    }
  }

  if (stage === 'codes') {
    // Recovery codes get their own modal (shared with the regenerate flow)
    // instead of a third state inline here.
    return <MfaRecoveryCodesModal codes={recoveryCodes} onClose={onEnabled} />
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Enable two-factor authentication" tabIndex={-1}>
        <h2>Enable two-factor authentication</h2>

        {stage === 'loading' && <p className="muted">Preparing setup…</p>}

        {stage === 'error' && (
          <>
            <div className="form-error">{error}</div>
            <button className="btn full" onClick={onClose} style={{ marginTop: 10 }}>Close</button>
          </>
        )}

        {stage === 'scan' && (
          <form onSubmit={submitCode}>
            <p className="muted" style={{ fontSize: 13.5 }}>
              Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy, etc).
            </p>
            <img src={qrDataUri} alt="Authenticator setup QR code" style={{ display: 'block', margin: '10px auto', width: 200, height: 200 }} />
            <p className="muted" style={{ fontSize: 12.5, textAlign: 'center', wordBreak: 'break-all' }}>
              Can't scan? Enter this code manually: <code>{secret}</code>
            </p>
            <div className="field">
              <label>Enter the 6-digit code from your app</label>
              <input className="inp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value)} required />
            </div>
            {error && <div className="form-error">{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn small outline" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn small" disabled={busy}>{busy ? 'Verifying…' : 'Verify and enable'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
